import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Rate a tenant.
 * Endpoint: POST /api/tenants/{tenantUserId}/ratings
 * @param {string} tenantUserId - The ID of the tenant user to rate
 * @param {Object} ratingData - The rating data
 * @param {string} ratingData.propertyId - The property ID
 * @param {number} ratingData.score - The rating score (e.g., 1-5)
 * @param {string} ratingData.message - The rating message
 * @returns {Promise<Object>} The API response
 */
export const rateTenant = async (tenantUserId, ratingData) => {
  if (!tenantUserId) {
    throw new Error('Tenant user ID is required for rating.');
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/${encodeURIComponent(tenantUserId)}/ratings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ratingData),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to save tenant rating';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('rateTenant error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
/**
 * Get tenant score and rating history by code.
 * Endpoint: GET /api/tenants/score-by-code?code={tenantCode}&monthsBack={monthsBack}
 * @param {string} tenantCode
 * @param {number} [monthsBack=12]
 * @returns {Promise<Object>}
 */
export const getTenantScoreByCode = async (tenantCode, monthsBack = 12) => {
  if (!tenantCode) {
    throw new Error('Tenant code is required');
  }

  try {
    const params = new URLSearchParams({
      code: tenantCode,
      monthsBack: String(monthsBack),
    });

    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/score-by-code?${params.toString()}`,
      { method: 'GET' },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch tenant score';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getTenantScoreByCode error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
